<script setup lang="ts">
	import { type CSSProperties, ref } from "vue";
	import { NSwitch, NDropdown, NButton, NAvatar } from "naive-ui";
	import { useRouter } from "vue-router";
	import { useI18n } from "vue-i18n";
	import { useAuthStore } from "@/store/authStore";
	import { AuthRouteNameEnum } from "@/shared/enums/routes/auth.enum.ts";
	import { IPLogoGradient, MoonIcon, SunIcon, LogoutIcon, DropdownArrowIcon } from "@/components/icons";
	import LanguageSelect from "@/components/LanguageSelect.vue";

	const { t } = useI18n();
	const router = useRouter();
	const authStore = useAuthStore();
	const isDark = ref<boolean>(false);

	const themeChanged = (value: boolean) => {
		let theme_colors = [
			"blue-gradient-from",
			"blue-gradient-to",

			"gs-100",
			"gs-200",
			"gs-300",
			"gs-400",

			"blue",
			"green",
			"orange",
			"red",

			"bg-primary",
			"bg-secondary",
			"bg-blue",
			"bg-green",
			"bg-orange",
			"bg-red",
		];

		if (value) {
			theme_colors.forEach((color) => {
				document.documentElement.style.setProperty(
					`--${color}`,
					getComputedStyle(document.documentElement).getPropertyValue(`--${color}-dark`)
				);
			});
		} else {
			theme_colors.forEach((color) => {
				document.documentElement.style.setProperty(
					`--${color}`,
					getComputedStyle(document.documentElement).getPropertyValue(`--${color}-light`)
				);
			});
		}
	};

	const railStyle = ({ focused, checked }: { focused: boolean; checked: boolean }) => {
		const style: CSSProperties = {};
		if (checked) {
			style.background = "var(--blue)";
			if (focused) style.boxShadow = "none";
			else style.background = "var(--blue)";
		}
		if (focused) {
			style.boxShadow = "none";
		}
		return style;
	};

	// The complete-profile / account-status screens run before the account is
	// active, so profile and notifications aren't reachable yet — the only user
	// action that makes sense in this stripped-down header is signing out.
	const userDropdownOptions = [
		{
			label: t("auth.sign_out"),
			icon: LogoutIcon,
			key: "auth.logout",
		},
	];

	const logout = (): void => {
		authStore.$patch((store) => {
			store.token = "";
			store.user = null;
		});
		router.push({ name: AuthRouteNameEnum.login });
	};

	const handleSelectUserDropdown = (key: string | number) => {
		if (key === "auth.logout") logout();
	};
</script>
<template>
	<div class="header">
		<div class="header_left">
			<div class="header_logo">
				<IPLogoGradient />
			</div>
		</div>
		<div class="header_right">
			<div class="header_mode-switcher">
				<n-switch
					:rail-style="railStyle"
					class="mode-switch"
					v-model:value="isDark"
					@update:value="themeChanged"
					size="large">
					<template #checked-icon>
						<MoonIcon />
					</template>
					<template #unchecked-icon>
						<SunIcon />
					</template>
				</n-switch>
			</div>
			<div class="header_language">
				<LanguageSelect />
			</div>
			<div class="header_user">
				<n-dropdown
					class="user-dropdown-menu"
					trigger="click"
					:options="userDropdownOptions"
					@select="handleSelectUserDropdown">
					<n-button text :render-icon="DropdownArrowIcon" icon-placement="right">
						<n-avatar round color="var(--bg-blue)" style="color: var(--blue)" :size="32">JT</n-avatar>
					</n-button>
				</n-dropdown>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
	@use "sass:map";

	.mode-switch.n-switch .n-switch__rail .n-switch__button-icon {
		padding: 0.4rem;
		color: var(--blue);
	}

	.header {
		@include flex(0, row, space-between, center);
		padding: 24px 32px;
		position: fixed;
		width: 100%;
		height: 96px;
		left: 0px;
		top: 0px;
		z-index: 99;

		&_logo {
			@include flex(0, row, center, center);
			border-radius: 0.8rem;
			padding: 1rem;
		}

		&_left {
			@include flex(3.2rem, row, start, center);
		}

		&_right {
			@include flex(1.6rem, row, start, center);
		}

		&_user {
			@include flex(0, row, center, center);
		}
	}

	.user-dropdown-menu.n-dropdown-menu {
		--n-divider-color: var(--gs-400) !important;
		--n-color: var(--bg-primary) !important;
		--n-prefix-color: var(--gs-300) !important;
		--n-option-text-color: var(--gs-100) !important;
		--n-option-text-color-hover: var(--blue-gradient-from) !important;
		--n-option-color-hover: var(--bg-secondary) !important;
		--n-padding: 0.8rem !important;
		--n-border-radius: 0.8rem !important;

		border-radius: 1.2rem !important;
	}
</style>
@/components/icons
